import React, { createContext, useCallback, useContext, useState } from "react";
import axios from "axios";

import { API } from "@/lib/api";

/**
 * Autenticação do admin por PIN — compartilhada entre todas as páginas
 * /admin/* via Context, pra não pedir o PIN de novo a cada clique no menu
 * lateral (isso ainda seria uma navegação client-side dentro do mesmo
 * carregamento de página).
 *
 * Segurança: o PIN é mantido SÓ em memória (state do Provider) para a
 * sessão atual. Um refresh de página sempre exige novo login — evita risco
 * de exfiltração via storage persistente (sessionStorage/localStorage) em
 * caso de XSS. Não altere essa propriedade sem revisar docs/PRD.md RF-A2/RNF-S1.
 */
const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);

  const verify = useCallback(async (p) => {
    try {
      await axios.post(`${API}/admin/login`, { pin: p });
      return true;
    } catch {
      return false;
    }
  }, []);

  const login = useCallback(
    async (candidate) => {
      const ok = await verify(candidate);
      if (ok) {
        setPin(candidate);
        setAuthed(true);
      }
      return ok;
    },
    [verify]
  );

  const logout = useCallback(() => {
    setPin("");
    setAuthed(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ pin, authed, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

/** Usar dentro de <AdminAuthProvider> (montado em AdminLayout.jsx). */
export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth precisa estar dentro de <AdminAuthProvider>");
  return ctx;
}
